import type { TableColumn, TableFieldSchema } from '@carboncopyco/editor-utils';
import {
  type CellContext,
  type RowData,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { TableCell } from '../TableCell';
import type { BaseFieldProps } from '../types';
import { useDebouncedValue } from '../useDebouncedValue';
import type { SpreadsheetAPI } from './ExportToSpreadsheet';
import { HeaderCell } from './HeaderCell';
import { RowActionsCell } from './RowActionsCell';
import { TableFieldPanel } from './TableFieldPanel';

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    updateData: (
      data: Pick<
        CellContext<Record<string, unknown>, unknown>,
        'row' | 'column'
      > & {
        value: unknown;
        accessorKey: string;
      }
    ) => void;
    updateColumn?: (data: {
      values: unknown[];
      accessorKey: string;
    }) => void;
  }
}

export interface TableFieldProps
  extends BaseFieldProps<TableFieldSchema, Record<string, unknown>[]> {
  spreadsheetApi: SpreadsheetAPI;
}

const rowActionsColumnId = 'rowActions';

export const TableField = ({
  value,
  field,
  readOnly,
  editableField,
  shouldTakeUpRemainingSpace,
  onChangeField,
  onChangeValue,
  onRemoveField,
}: TableFieldProps) => {
  const [innerValue, setInnerValue] = useDebouncedValue(value, onChangeValue);

  const columns = useMemo(
    () =>
      !readOnly
        ? [
            {
              id: 'rowActions',
              cell: ({ row, table }) => (
                <RowActionsCell
                  row={row}
                  value={innerValue}
                  onChangeValue={setInnerValue}
                  isLastRow={table.getRowModel().rows.length === 1}
                />
              ),
            },
            ...field.uiSchema.options.columns,
          ]
        : field.uiSchema.options.columns,
    [innerValue]
  );

  const updateTableData = useCallback(
    ({ row, value: cellValue, accessorKey }) => {
      const clonedValue = structuredClone(value);
      clonedValue[row.index][accessorKey] = cellValue;
      setInnerValue?.(clonedValue);
    },
    [value, setInnerValue]
  );
  const updateColumn = useCallback(
    ({ accessorKey, values }) => {
      const clonedValue = structuredClone(value);
      clonedValue.forEach((_, index) => {
        clonedValue[index][accessorKey] = values[index];
      });

      setInnerValue?.(clonedValue);
    },
    [value, setInnerValue]
  );

  const table = useReactTable({
    data: innerValue,
    defaultColumn: {
      cell: ({ getValue, row, column, table }) => {
        const accessorKey = (column.columnDef as any).accessorKey ?? '';
        const jsonSchema = field.jsonSchema?.items?.properties?.[accessorKey];
        return (
          <TableCell
            value={getValue()}
            onChange={(cellValue) => {
              table.options.meta?.updateData({
                row,
                column,
                value: cellValue,
                accessorKey,
              });
            }}
            jsonSchema={jsonSchema}
            readOnly={readOnly}
          />
        );
      },
    },
    meta: {
      updateData: updateTableData,
      updateColumn,
    },
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const changeColumnHeader = (
    columnId: TableColumn['id'],
    newValue: TableColumn['header']
  ) => {
    const updatedColumns = field.uiSchema.options.columns.map((column) =>
      column.id === columnId ? { ...column, header: newValue } : column
    );
    onChangeField?.({
      ...field,
      uiSchema: {
        ...field.uiSchema,
        options: {
          ...field.uiSchema.options,
          columns: updatedColumns,
        },
      },
    });
  };
  return (
    <TableFieldPanel
      editable={editableField}
      field={field}
      getTableInstance={() => table}
      onChange={onChangeField}
      onUpdateTable={setInnerValue}
      onRemoveField={onRemoveField}
    >
      <div
        className={classNames(
          'overflow-scroll rounded-md text-ds-natural-600',
          !shouldTakeUpRemainingSpace && 'max-h-[200px]'
        )}
      >
        <table className="relative text-xs border-none">
          <thead className="sticky top-0 text-left">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <HeaderCell
                      key={header.id}
                      header={header}
                      editableField={editableField}
                      onBlur={(e) => {
                        changeColumnHeader?.(
                          header.id,
                          e.currentTarget.textContent
                        );
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </HeaderCell>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white">
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="group/tr"
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="group/td whitespace-nowrap p-0"
                  >
                    <div
                      className={classNames(
                        'min-h-[28px] border border-ds-natural-150 group-last-of-type/tr:group-first-of-type/td:rounded-bl-md group-last-of-type/tr:group-last-of-type/td:rounded-br-md',
                        cell.column.id === rowActionsColumnId
                          ? 'w-full flex'
                          : 'py-1 px-2.5'
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TableFieldPanel>
  );
};
